import React, { forwardRef, useCallback, useEffect, useState } from "react";

import { getClassNames } from "../../utils";
import "./Checkbox.css";

import { CHECKBOX_SIZE_DEFAULT, CHECKBOX_SIZES } from "./constants";

import withTheme from "../../hocs/withTheme";
import uid from "../../utils/uid";
import FormControlMessage from "../FormControlMessage/FormControlMessage";

type CheckboxSize = (typeof CHECKBOX_SIZES)[number];

interface CheckboxProps {
    label: string;
    description?: string | null;
    autoFocus?: boolean;
    size?: CheckboxSize;
    disabled?: boolean;
    defaultChecked?: boolean | string;
    error?: boolean | string;
    checked?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    innerRef?: React.Ref<HTMLInputElement>;
    isInQuestionnaire?: boolean;
    onlyExternalState?: boolean;
    id?: string;
}

const Checkbox = (props: CheckboxProps) => {
    const {
        description = null,
        autoFocus = false,
        size = CHECKBOX_SIZE_DEFAULT,
        disabled = false,
        defaultChecked = false,
        error = false,
        checked = false,
        onChange = (event) => event,
        innerRef: ref = null,
        isInQuestionnaire = false,
        onlyExternalState = false,
        id = "",
        label,
        ...rests
    } = props;

    const [value, setValue] = useState<boolean>(
        Boolean(checked || defaultChecked),
    );

    useEffect(() => {
        setValue(Boolean(checked || defaultChecked));
    }, [checked, defaultChecked]);

    const classNameCheckboxWrapper = getClassNames(
        "checkbox-wrapper",
        { [`checkbox-${size}`]: size },
        { "checkbox-error": error },
        { "checkbox-wrapper-disabled": disabled },
    );

    const classNameCheckbox = getClassNames("checkbox", {
        "checkbox-checked": value,
    });

    const onChangeHandlar = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (!onlyExternalState) {
                setValue(event.target.checked);
            }

            if (typeof onChange === "function") {
                onChange(event);
            }
        },
        [onChange, onlyExternalState],
    );

    const uIdCheckbox = id || uid("checkbox");

    return (
        <div
            className={getClassNames("checkbox", {
                "checkbox-wrapper-disabled": disabled,
                "form-group_invalid": error,
            })}
        >
            <input
                {...rests}
                ref={ref}
                type="checkbox"
                tabIndex={0}
                aria-checked={value}
                className="checkbox-input"
                onChange={onChangeHandlar}
                checked={value}
                disabled={disabled}
                id={uIdCheckbox}
            />

            <div className="checkbox-inner">
                <label className="checkbox-label" htmlFor={uIdCheckbox}>
                    <span
                        className={
                            label?.includes("\n") ? "label_with_new_line" : ""
                        }
                    >
                        {label}
                    </span>
                </label>

                {description && (
                    <span className="checkbox-description">{description}</span>
                )}

                {error && !isInQuestionnaire && (
                    <FormControlMessage>{error}</FormControlMessage>
                )}
            </div>
        </div>
    );
};

const ForwardedCheckbox = forwardRef<HTMLInputElement, CheckboxProps>(
    (props, ref) =>
        withTheme(Checkbox)({
            ...props,
            innerRef: ref,
        }),
);

export default ForwardedCheckbox;
